import { BaseModel } from './BaseModel';

const CHANNEL_TYPE = 'group_channels';

export class Message extends BaseModel {
  static get entity() {
    return 'messages';
  }

  static get fk() {
    return 'message_id';
  }

  static get primaryKey() {
    return 'messageId';
  }

  static fields() {
    return {
      appleCriticalAlertOptions: this.attr(null),
      channelType: this.string(''),
      channelUrl: this.string(''),
      createdAt: this.number(0),
      customType: this.string(''),
      data: this.string(''),
      errorCode: this.number(0),
      isOperatorMessage: this.boolean(false),
      mentionType: this.string(''),
      mentionedUsers: this.attr([]),
      message: this.string(''),
      messageId: this.number(0),
      messageSurvivalSeconds: this.number(-1),
      messageType: this.string(''),
      metaArrays: this.attr([]),
      ogMetaData: this.attr(null),
      parentMessageId: this.number(0),
      parentMessageText: this.attr(null),
      plugins: this.attr([]),
      poll: this.attr(null),
      reactions: this.attr([]),
      reqId: this.string(''),
      requestState: this.string(''),
      requestedMentionUserIds: this.attr([]),
      sendingStatus: this.string(''),
      silent: this.boolean(false),
      threadInfo: this.attr(null),
      translations: this.attr(null),
      updatedAt: this.number(0),
      _sender: this.attr(''),
      url: this.string(''),
      name: this.string(''),
      size: this.number(0),
      type: this.string('')
    };
  }

  static inChannel(channelUrl) {
    return this.query().where('channelUrl', channelUrl).get();
  }

  get isPlainMessage() {
    return this.messageType === 'user';
  }

  get isFileMessage() {
    return this.messageType === 'file';
  }

  static get globalApiConfig() {
    return {
      baseURL: `https://api-${process.env.VUE_APP_SENDBIRD_APP_ID}.sendbird.com/v3`,
      headers: {
        'Api-Token': process.env.VUE_APP_SENDBIRD_API_TOKEN
      }
    };
  }

  static apiConfig = {
    actions: {
      /**
       * https://sendbird.com/docs/chat/v3/platform-api/guides/messages#2-list-messages
       *
       * @param {String} channel_url required
       * @param {Number} message_ts required either message_ts or message_id, Timestamp to be the reference point of the query, in Unix milliseconds.
       * @param {Number} message_id required either message_ts or message_id, Unique ID of the message to be the reference point of the query
       * @param {Object} params query params from sendbird
       * @returns {Array}
       */
      getMessagesList(channel_url, params) {
        return this.get(`/${CHANNEL_TYPE}/${channel_url}/messages`, { params });
      },

      /**
       * https://sendbird.com/docs/chat/v3/platform-api/guides/messages#2-send-a-message
       *
       * @param {String} channel_url required
       * @param {Object} body
       * @param {String} body.message_type required, Specifies the type of the message as MESG.
       * @param {String} body.user_id required, Specifies the user ID of the sender.
       * @param {String} body.message required, Specifies the content of the message.
       * @param {String} body.file required, If send file message
       * @returns {Object}
       */
      sendMessage(channel_url, body) {
        if (body.file) {
          return this.post(`/${CHANNEL_TYPE}/${channel_url}/messages`, body, {
            headers: {
              'Content-Type': 'multipart/form-data;'
            }
          });
        } else {
          return this.post(`/${CHANNEL_TYPE}/${channel_url}/messages`, body);
        }
      },

      /**
       * https://sendbird.com/docs/chat/v3/platform-api/guides/messages#2-update-a-message
       *
       * @param {String} channel_url required
       * @param {Number} message_id required
       * @param {Object} body
       * @param {String} body.message_type required, Specifies the type of the message as MESG.
       * @param {String} body.message optional
       * @returns {Object} status success or fail
       */
      updateMessage(channel_url, message_id, body) {
        return this.put(`/${CHANNEL_TYPE}/${channel_url}/messages/${message_id}`, body);
      },

      /**
       * https://sendbird.com/docs/chat/v3/platform-api/guides/messages#2-delete-a-message
       *
       * @param {String} channel_url required
       * @param {Number} message_id required
       * @returns {Empty} If successful, this action returns an empty response body.
       */
      deleteMessage(channel_url, message_id) {
        return this.delete(`/${CHANNEL_TYPE}/${channel_url}/messages/${message_id}`);
      }
    }
  };
}

export default Message;
